/**
 * @fileoverview Carrega a entrega (deliverable) do PRÓPRIO aluno logado para uma
 * atividade do projeto Arc e expõe a ação de submeter.
 *
 * Contrato de backend (confirmado):
 * 1. `GET /api/session_meeting_activities/:activityId` (via `getActivity`) →
 *    retorna `studentTask.id` — a definição da task da atividade (has_one).
 * 2. `GET /api/student_tasks/:studentTaskId` — escopado ao `current_user`; quando
 *    `logged_as_student?`, CRIA automaticamente uma `student_task_delivery` aberta
 *    e retorna `studentTaskDeliveryId`, `projectId`, `status`, `deadline`,
 *    `planningFiles`, `studentTaskDeliveryLast`, `sessionMeetingActivity{...}`.
 *    Entrega única (AR-741): quando `studentTaskDeliveryLast` existe (a entrega
 *    `done` real), normalizamos `studentTaskDeliveryId` para o id dela — uploads,
 *    remoções e o PUT miram sempre o mesmo registro, nunca o descartável.
 * 3. Submeter: `PUT api/student_task_deliveries/:deliveryId { description }` →
 *    marca a entrega como `done`.
 * 4. Links (AR-677/AR-678): `POST /api/student_task_deliveries/:deliveryId/links
 *    { link: { url } }` → 201 `{ id, url, createdAt }` (422 = URL inválida);
 *    `DELETE /api/student_task_deliveries/:deliveryId/links/:id` → 204. Sempre
 *    no MESMO deliveryId efetivo normalizado acima (AR-741). A lista local de
 *    links é estado próprio (não refazemos o GET, que criaria outra entrega).
 *
 * Defensivo: toda etapa pode falhar sem derrubar a tela (flag `cancelled`
 * anti-race, `errored` para estados de tela branca, optional chaining).
 */

import { useCallback, useEffect, useState } from 'react'
import axios from 'axios'
import api from 'helpers/api'
import { getActivity } from 'pages/NewProject/service/projects'
import type { AddLinkOutcome } from './linkUtils'

export interface StudentDeliverableFile {
  readonly id: string
  readonly name: string
  readonly fileId: string
  readonly fileType: string
  readonly downloadUrl: string
}

export interface StudentDeliverableLink {
  readonly id: string
  readonly url: string
  readonly createdAt: string
}

export interface StudentDeliverableData {
  readonly id: string
  readonly title: string
  readonly description: string
  readonly deadline: string
  readonly status: string
  readonly overdue: boolean
  readonly projectId: string
  readonly studentTaskDeliveryId: string
  readonly planningFiles: StudentDeliverableFile[]
  // Raiz do GET: `student_task_delivery_links` (camelizado pelo humps) — a
  // chave `links` no top-level já era usada por links HATEOAS nesse JSON.
  readonly studentTaskDeliveryLinks?: StudentDeliverableLink[]
  readonly sessionMeetingActivity: {
    readonly title: string
    readonly projectName: string
    readonly sessionMeetingTitle: string
    readonly sessionMeetingDate: string
    readonly tutor: string
  }
  readonly studentTaskDeliveryLast?: {
    readonly id: string
    readonly description: string
    readonly createdAt: string
    readonly planningFiles: StudentDeliverableFile[]
    readonly links?: StudentDeliverableLink[]
  }
}

interface UseStudentDeliverableResult {
  loading: boolean
  errored: boolean
  submitting: boolean
  submitted: boolean
  data: StudentDeliverableData | null
  links: StudentDeliverableLink[]
  submit: (description?: string) => Promise<boolean>
  addLink: (url: string) => Promise<AddLinkOutcome>
  removeLink: (linkId: string) => Promise<boolean>
}

const STUDENT_TASK_DONE_STATUS = 'done'

// O aluno "entregou" quando já existe uma entrega concluída (a rota cria sempre
// uma entrega aberta nova a cada GET, então o status corrente fica `pending`/
// `review`; o sinal confiável é a última entrega `done`).
export function isDeliverableSubmitted(data: StudentDeliverableData | null): boolean {
  return Boolean(data?.studentTaskDeliveryLast) || data?.status === STUDENT_TASK_DONE_STATUS
}

export function useStudentDeliverable(activityId: string, opened: boolean): UseStudentDeliverableResult {
  const [loading, setLoading] = useState(true)
  const [errored, setErrored] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [justSubmitted, setJustSubmitted] = useState(false)
  const [data, setData] = useState<StudentDeliverableData | null>(null)
  const [links, setLinks] = useState<StudentDeliverableLink[]>([])

  useEffect(() => {
    if (!opened || !activityId) return

    let cancelled = false
    setLoading(true)
    setErrored(false)
    setJustSubmitted(false)
    setData(null)
    setLinks([])

    const load = async () => {
      try {
        const activity = await getActivity(activityId)
        const studentTaskId: string | undefined = activity?.studentTask?.id

        if (!studentTaskId) {
          if (!cancelled) {
            setErrored(true)
            setData(null)
          }
          return
        }

        const response = await api.get(`/api/student_tasks/${studentTaskId}`)
        const raw = (response?.data as StudentDeliverableData | undefined) ?? null
        // Entrega única (AR-741): reusar a entrega done existente no lugar da
        // aberta descartável que o GET cria — filesPath/GDrive/PUT no mesmo id.
        if (!cancelled) {
          setData(
            raw
              ? { ...raw, studentTaskDeliveryId: raw.studentTaskDeliveryLast?.id || raw.studentTaskDeliveryId }
              : null,
          )
          // Links da entrega EFETIVA (a done, quando existe) — mesmo id do PUT.
          // Com entrega done, NUNCA cair para os links da entrega aberta.
          const last = raw?.studentTaskDeliveryLast
          setLinks(last ? last.links ?? [] : raw?.studentTaskDeliveryLinks ?? [])
        }
      } catch (error) {
        console.error('Error loading student deliverable:', error)
        if (!cancelled) {
          setErrored(true)
          setData(null)
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
    }
  }, [opened, activityId])

  const submit = useCallback(
    async (description = ''): Promise<boolean> => {
      const deliveryId = data?.studentTaskDeliveryId
      // O backend exige `description` presente no update (marca `done` e atualiza
      // `updated_at`, tornando esta entrega — com os arquivos — a mais recente que
      // o professor enxerga). Sem isso a entrega fica órfã. Não recarregamos depois:
      // um novo GET criaria outra entrega vazia que esconderia os arquivos.
      if (!deliveryId || !description) return false

      setSubmitting(true)
      try {
        await api.put(`/api/student_task_deliveries/${deliveryId}`, { description })
        setJustSubmitted(true)
        return true
      } catch (error) {
        console.error('Error submitting deliverable:', error)
        return false
      } finally {
        setSubmitting(false)
      }
    },
    [data],
  )

  // Espelha o contrato AR-677: POST cria o link na entrega efetiva; 422 =
  // URL inválida/quebrada (erro inline no modal); demais falhas = 'error'.
  // O interceptor de `helpers/api` RESOLVE respostas 401 — por isso só é
  // sucesso com 201 real E o link criado no body.
  const addLink = useCallback(
    async (url: string): Promise<AddLinkOutcome> => {
      const deliveryId = data?.studentTaskDeliveryId
      if (!deliveryId || !url) return 'error'

      try {
        const response = await api.post(`/api/student_task_deliveries/${deliveryId}/links`, {
          link: { url },
        })
        const link = response?.data as StudentDeliverableLink | undefined
        if (response?.status !== 201 || !link?.id) return 'error'
        setLinks(current => [...current, link])
        return 'success'
      } catch (error) {
        console.error('Error adding deliverable link:', error)
        if (axios.isAxiosError(error) && error.response?.status === 422) return 'invalid'
        return 'error'
      }
    },
    [data],
  )

  const removeLink = useCallback(
    async (linkId: string): Promise<boolean> => {
      const deliveryId = data?.studentTaskDeliveryId
      if (!deliveryId || !linkId) return false

      try {
        const response = await api.delete(`/api/student_task_deliveries/${deliveryId}/links/${linkId}`)
        // 401 é RESOLVIDO pelo interceptor — só remove da lista com 204 real.
        if (response?.status !== 204) return false
        setLinks(current => current.filter(link => link.id !== linkId))
        return true
      } catch (error) {
        console.error('Error removing deliverable link:', error)
        return false
      }
    },
    [data],
  )

  const submitted = justSubmitted || isDeliverableSubmitted(data)

  return { loading, errored, submitting, submitted, data, links, submit, addLink, removeLink }
}
